import { c as _c } from "react/compiler-runtime";
import { Box, type BoxProps, HStack, Text } from '@chakra-ui/react';
import type { CrmPalette } from '@src/components/sco/Admin/AiCrm/crmLane';
import React, { type ReactNode } from 'react';

/**
 * The CRM surfaces. They sit on the admin shell's gray palette and climb its ladder:
 * page (colorPalette.bg), card (panel), then tile or field (container). Accents recolor
 * a small region with `colorPalette`. No hex, no raw alpha.
 */

/** Wrap a CRM region so its tokens do not depend on what is around it. */
export function CrmRoot(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Box color="fg" colorPalette="gray" minW={0} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/** The centered reading column the desk pages share. */
export function CrmColumn(props) {
  const $ = _c(8);
  let rest;
  let wide;
  if ($[0] !== props) {
    ({
      wide,
      ...rest
    } = props);
    $[0] = props;
    $[1] = rest;
    $[2] = wide;
  } else {
    rest = $[1];
    wide = $[2];
  }
  const t0 = wide ? "1240px" : "1080px";
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: 4,
      md: 6
    };
    t2 = {
      base: 4,
      md: 6
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== rest || $[6] !== t0) {
    t3 = <Box maxW={t0} mx="auto" px={t1} py={t2} w="full" {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export const CrmCard = React.forwardRef<HTMLDivElement, BoxProps>(function CrmCard(props, ref) {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <Box ref={ref} bg="colorPalette.panel" borderColor="colorPalette.border" borderRadius="xl" borderWidth="1px" minW={0} {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});

/** A tile or field surface, one step up the ladder from a card. */
export function CrmInset(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Box bg="colorPalette.container" borderColor="colorPalette.border" borderRadius="lg" borderWidth="1px" minW={0} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * A tinted note: a colored edge, a tinted field, and text. `tone` is a color palette.
 * Used for "why this stopped", warnings, and the gold "rules decide" line.
 */
export function CrmCallout(props) {
  const $ = _c(19);
  let children;
  let icon;
  let rest;
  let t0;
  let title;
  let tone;
  if ($[0] !== props) {
    ({
      children,
      edge: t0,
      icon,
      title,
      tone,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = icon;
    $[3] = rest;
    $[4] = t0;
    $[5] = title;
    $[6] = tone;
  } else {
    children = $[1];
    icon = $[2];
    rest = $[3];
    t0 = $[4];
    title = $[5];
    tone = $[6];
  }
  const edge = t0 === undefined ? "left" : t0;
  const t1 = edge === "left" ? "colorPalette.border" : undefined;
  const t2 = edge === "left" ? "3px" : undefined;
  let t3;
  if ($[7] !== icon || $[8] !== title) {
    t3 = title ? <HStack color="colorPalette.fg" gap={2} mb={1}>{icon}<Text textStyle="labelXs">{title}</Text></HStack> : null;
    $[7] = icon;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children) {
    t4 = <Box color="fg" fontSize="sm" lineHeight="1.6">{children}</Box>;
    $[10] = children;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== rest || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== tone) {
    t5 = <Box bg="colorPalette.subtle" borderColor="colorPalette.border/40" borderLeftColor={t1} borderLeftWidth={t2} borderRadius="lg" borderWidth="1px" colorPalette={tone} px={4} py={3} {...rest}>{t3}{t4}</Box>;
    $[12] = rest;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = tone;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}